import { useState, useEffect, useCallback } from "react";
import Navbar from "@/components/Navbar";
import { getStats, getActivity, invalidatePublicData } from "@/lib/api";
import { useRealtime } from "@/hooks/useRealtime";
import { Link } from "react-router-dom";
import { Flame, Users, Zap, Swords, Crown, Trophy } from "lucide-react";
import SeoContent from "@/components/SeoContent";

function StatCard({ icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {icon} {label}
      </div>
      <div className={`mt-1 font-mono text-2xl font-bold ${accent || ""}`}>{value}</div>
    </div>
  );
}

export default function StatsPage() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);

  const load = useCallback(async () => {
    const [statsResult, activityResult] = await Promise.allSettled([getStats(), getActivity(100)]);
    if (statsResult.status === "fulfilled") setStats(statsResult.value);
    if (activityResult.status === "fulfilled") {
      setActivity(activityResult.value.filter((a) => a.kind === "flip" || a.kind === "new_number_one"));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useRealtime(
    useCallback((msg) => {
      if (msg.type === "leaderboard_update") {
        invalidatePublicData(["stats"]);
        invalidatePublicData(["activity"]);
        load();
      }
    }, [load])
  );

  const fmtMoney = (n) => `$${(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <section className="mx-auto max-w-6xl px-4 pt-10">
        <h1 className="font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">
          The board, <span className="text-gold">in numbers.</span>
        </h1>
        <p className="mt-2 max-w-lg text-sm text-zinc-400">
          Every dollar is verified on-chain. This page updates in real time.
        </p>

        {stats && (
          <>
            <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="stats-grid">
              <StatCard icon={<Flame className="h-3 w-3" />} label="Total backed" value={fmtMoney(stats.total_backed_usd)} accent="text-gold" />
              <StatCard icon={<Zap className="h-3 w-3" />} label="Volume 24h" value={fmtMoney(stats.volume_24h)} />
              <StatCard icon={<Users className="h-3 w-3" />} label="Backers" value={(stats.total_contributors || 0).toLocaleString("en-US")} />
              <StatCard icon={<Swords className="h-3 w-3" />} label="Flips 24h" value={String(stats.flips_24h || 0)} />
              <StatCard icon={<Trophy className="h-3 w-3" />} label="Active entities" value={String(stats.active_entities || 0)} />
              <StatCard icon={<Zap className="h-3 w-3" />} label="Contributions 24h" value={String(stats.contributions_24h || 0)} />
            </div>

            {stats.king_slug && (
              <Link
                to={`/token/${stats.king_slug}`}
                data-testid="king-banner"
                className="mt-6 flex items-center justify-between rounded-2xl border border-gold/30 bg-gold/[0.06] px-5 py-4 transition-colors hover:bg-gold/[0.12]"
              >
                <span className="flex items-center gap-2 font-display text-sm font-black uppercase tracking-wide text-gold">
                  <Crown className="h-4 w-4 fill-gold" strokeWidth={1.5} /> Current king
                </span>
                <span className="font-mono text-sm font-bold">{stats.king_slug} →</span>
              </Link>
            )}

            {stats.last_flip && (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-4" data-testid="last-flip">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Last flip</div>
                <div className="mt-1 text-sm">
                  <b>{stats.last_flip.by_label}</b> flipped <b>{stats.last_flip.entity_label}</b>
                  {" "}· #{stats.last_flip.old_rank} → #{stats.last_flip.new_rank}
                  {" "}with {fmtMoney(stats.last_flip.usd_value)}
                </div>
              </div>
            )}
          </>
        )}

        <h2 className="mt-10 font-display text-xl font-black uppercase tracking-tight">Rank wars</h2>
        <div className="mt-4 space-y-2 pb-24" data-testid="rank-wars">
          {activity.length === 0 && (
            <div className="rounded-2xl border border-white/10 bg-white/[0.02] px-5 py-6 text-center text-sm text-zinc-500">
              No flips yet — be the one who starts the war.
            </div>
          )}
          {activity.map((a) => (
            <div key={a.id} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.02] px-4 py-3 text-sm">
              <span>{a.text}</span>
              {a.meta?.slug && (
                <Link to={`/token/${a.meta.slug}`} className="shrink-0 pl-3 font-mono text-xs text-gold hover:underline">view →</Link>
              )}
            </div>
          ))}
        </div>
      </section>
      <SeoContent variant="stats" />
    </div>
  );
}
